// @ts-strict-ignore
import { AfterViewChecked, ChangeDetectorRef, Component, effect, EventEmitter, inject, Input, OnDestroy, OnInit, Output, Signal } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { AlertHelper } from '@shared/alert/alert.helper';
import { lastValueFrom, Observable } from 'rxjs';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomValidators } from '@shared/custom-validators';
import { ProductionPlantVO } from '@project/model/vo/production-plant.vo';
import { ProductionPlantDataService } from '@shared/service/production-plant-data.service';
import { ArticleDataService } from '@article/service/article-data.service';
import { ArticleTypeClassVO, ArticleVO } from '@article/model/vo/article.vo';
import { ProjectModel } from '@project/model/project.model';
import { ProjectType } from '../../../model/project-type';
import { IsProjectType } from '../../../model/is-project-type';
import { StringUtils } from '@shared/util/string-utils';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { MarkingSystemDataService } from '../../article/manager/marking-system-data.service';
import { CustomsTariffNumberVO, FinalizationPreselectionVO, ProjectFinalizationInfoVO, ProjectOptionVO } from '@project/model/vo/project.vo';
import { ProjectOptionUtils } from '../util/project-option.utils';
import { ProjectOptionDefIds } from '@project/components/project/constants/project-option-def-ids';
import { PropertyValueVO } from '../../article/model/vo/property.vo';
import { PropertyValueType } from '@project/constants/property-value-type';
import { CountryCodeVO } from '@shared/vo/country-code.vo';
import { isArray, range } from 'lodash';
import { map } from 'rxjs/operators';
import { ProjectDataService } from '../../../service/project-data.service';
import { ProjectService } from '../../import-export/service/project.service';
import { ProjectComponentPresenter } from '../../project-component.presenter';
import { ProjectUserModel } from '../../../model/project-user.model';
import { UserUtils } from '@user/service/user-utils';
import { UserModel } from '@user/model/user.model';
import { ArticleNumberLong } from '../../../model/article-number-long';
import { MdmGroupVO } from '@user/model/vo/user.vo';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserMdmService } from '@user/service/user-mdm.service';

@Component({
  standalone: false,
  selector: 'app-finalize-project',
  templateUrl: './finalize-project.component.html',
})
export class FinalizeProjectComponent implements OnInit, OnDestroy, AfterViewChecked {

  @Input() mcsGds221LtlExport: boolean = false;
  @Input() fNumber: string;
  @Input() markingSystemExport: boolean = false;
  @Input() articleNumber: string;
  @Input() articleNumberLong: ArticleNumberLong;
  @Input() articleNumberExtension: string;
  @Input() drawingNumber: string;
  @Input() documentVersion: number;
  @Input() legacyExport: boolean = false;
  @Input() tcVersion: number;
  @Input() onlyDrawing: boolean = false;
  @Input() isArticleNumberGenerated: boolean = false;
  @Input() showGenerateArticleNumberElement: boolean = false;
  @Input() useCustomArticleNumber: boolean = false;
  public maxArticleNumberChars: number = 80; // always using 80 as limit as it was practically done in flex version
  @Output() public finish = new EventEmitter<boolean>();

  private calculatedArticleNumberLong: ArticleNumberLong;
  private _currentArticleNumberLong: ArticleNumberLong;
  public articleNumberTextInputDisabled: boolean = false;

  public preselectGenerateArticleNumberElement: boolean = false;
  public form: FormGroup;
  public productionPlants: ProductionPlantVO[];
  public countryCodes: CountryCodeVO[];
  public typeClasses: ArticleTypeClassVO[];
  public mainNotes: {attributeId: number, displayName: string}[];
  public additionalNotes: {attributeId: number, displayName: string}[][] = [];
  public productFamilies: {attributeId: number, displayName: string}[];
  public tariffNumbers: CustomsTariffNumberVO[];
  public countryCodeManuallySelected: boolean = false;
  public isCountryCodeSelectorEnabled: boolean = true;
  public interNationalCountryCode: string = '';

  private readonly userMdmService = inject(UserMdmService);
  public allMdmGroups: Signal<MdmGroupVO[]>;

  public readonly articleNumberLongTextInputName: string = 'articleNumberLongTextInput';
  public readonly articleNumberTextInputName: string = 'articleNumberTextInput';
  public readonly articleNumberTextInputLegacyName: string = 'articleNumberTextInputLegacy';
  public readonly articleNumberExtensionInputName: string = 'articleNumberExtensionInput';

  public readonly typeClassIdComboboxName: string = 'typeClassIdCombobox';
  public readonly mainNoteComboboxName: string = 'mainNoteCombobox';
  public readonly additionalNoteComboboxName: string = 'additionalNoteCombobox';
  public readonly productFamilyComboboxName: string = 'productFamilyCombobox';
  public readonly numberOfAdditionalNotes: number = 12;
  public readonly additionalNoteComboboxNames = range(1, this.numberOfAdditionalNotes + 1)
    .map((number) => `additionalNoteCombobox${number.toString().padStart(2, '0')}`);
  public readonly productionPlantComboboxName: string = 'productionPlantCombobox';
  public readonly countryCodeComboboxName: string = 'countryCodeCombobox';
  public readonly stockSeparationComboboxName: string = 'stockSeparationCombobox';
  public readonly changeOrderNumberInputName: string = 'changeOrderNumberInput';
  public readonly tariffNumberComboboxName: string = 'tariffNumberCombobox';
  public readonly mdmGroupComboboxName: string = 'mdmGroupCombobox';

  public readonly markingSystemTypeClassId: string = '2X00203_KL_MAK';

  public currentTypeClassId: string;
  private preselectMainNote: boolean = false;
  private preselectProductFamily: boolean = false;
  private preselectAdditionalNotes: boolean = false;
  private projectFinalizationInfoVO: ProjectFinalizationInfoVO;
  private productionPlantProjectOption: ProjectOptionVO;
  private changeOrderNumberProjectOption: ProjectOptionVO;
  private stockSeparationProjectOption: ProjectOptionVO;
  private togglePdmModal = document.querySelector('#togglePdmModal');
  private duplicateFound: boolean = false;

  private updateSelectsWithNoSelectedValue: boolean = false;

  constructor(
    private bsModalRef: BsModalRef,
    private formBuilder: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private alertHelper: AlertHelper,
    private productionPlantDataService: ProductionPlantDataService,
    private articleDataService: ArticleDataService,
    private projectModel: ProjectModel,
    private markingSystemDataService: MarkingSystemDataService,
    private projectDataService: ProjectDataService,
    private projectService: ProjectService,
    private projectComponentPresenter: ProjectComponentPresenter,
    private projectUserModel: ProjectUserModel,
    private userModel: UserModel,
  ) {
    this.allMdmGroups = toSignal(this.userMdmService.getMdmGroupsAvailableForPdmExport());

    // Preselect the mdmGroup value when allMdmGroups is available
    effect(() => {
      const allMdmGroups: MdmGroupVO[] = this.allMdmGroups();
      const preselectedMdmGroup: MdmGroupVO = this.userMdmService.getPreselectedMdmGroupForPdmExport(allMdmGroups);
      this.form.controls.mdmGroupCombobox.setValue(preselectedMdmGroup);
    });
  }

  public get isMdmGroupVisible(): boolean {
    return this.userMdmService.canEditMdmGroupForPdmExport;
  }

  public compareMdmGroups(a: MdmGroupVO, b: MdmGroupVO): boolean {
    return MdmGroupVO.areEqual(a, b);
  }

  public get isCurrentArticleNumberLongEmpty() {
    return !this.currentArticleNumberLong || this.currentArticleNumberLong.isEmpty();
  }

  public get currentArticleNumberLong(): ArticleNumberLong {
    return this._currentArticleNumberLong;
  }

  private set currentArticleNumberLong(value: ArticleNumberLong) {
    this._currentArticleNumberLong = value;
  }

  public ngOnInit(): void {
    if (IsProjectType.markingSystem(this.projectModel.currentProjectType)) {
      this.markingSystemExport = true;
      this.checkForDuplicates();
    }
    this.isArticleNumberGenerated = this.projectModel.currentProject.isArticleNumberGenerated;
    this.currentArticleNumberLong = this.projectModel.currentProject.articleNumberLong;

    if (!this.markingSystemExport) {
      this.isCountryCodeSelectorEnabled = false;
    }

    if (this.fNumber && this.fNumber.length >= 1) {
      this.showGenerateArticleNumberElement = false;
      this.preselectGenerateArticleNumberElement = false;
      this.isCountryCodeSelectorEnabled = false;
    } else if (this.mcsGds221LtlExport && this.isArticleNumberGenerated) {
      this.showGenerateArticleNumberElement = false;
      this.preselectGenerateArticleNumberElement = false;
    } else if (this.mcsGds221LtlExport) {
      this.showGenerateArticleNumberElement = !this.legacyExport;
      this.preselectGenerateArticleNumberElement = !this.legacyExport;
    } else if (!this.legacyExport
      && !this.isArticleNumberGenerated
      && (
        IsProjectType.visibleMountingRail(this.projectModel.currentProject)
        || IsProjectType.freeMounting(this.projectModel.currentProject))) {
      this.showGenerateArticleNumberElement = true;
    }
    if (this.markingSystemExport || this.isArticleNumberGenerated || this.fNumber) {
      this.articleNumberTextInputDisabled = true;
    }

    this.productionPlantProjectOption = ProjectOptionUtils.findProjectOptionByIdentifier(
      this.projectModel.currentProject?.options, ProjectOptionDefIds.PRODUCTION_PLANT);
    this.changeOrderNumberProjectOption = ProjectOptionUtils.findProjectOptionByIdentifier(
      this.projectModel.currentProject?.options, ProjectOptionDefIds.CO_NUMBER);

    this.calculatedArticleNumberLong = this.currentArticleNumberLong;

    this.form = this.createForm();
    this.loadAdditionalData();

    this.togglePdmModal.classList.remove('d-none');
  }

  private createForm(): FormGroup<any> {
    const currentArticleNumber = this.getCurrentArticleNumber(
      this.fNumber,
      this.legacyExport,
      this.articleNumber,
      this.projectModel.currentProject.articleNumber,
      this.projectModel.currentProject.projectType,
      this.isArticleNumberGenerated,
    );

    let articleNumberExtensionInputDisabled: boolean = false;
    if (!this.legacyExport && this.fNumber) {
      articleNumberExtensionInputDisabled = true;
    }

    let changeOrderNumberInputValue: string = '';
    if (this.changeOrderNumberProjectOption != null) {
      changeOrderNumberInputValue = this.changeOrderNumberProjectOption.value;
    }

    const formData: any = {};
    if (this.markingSystemExport) {
      formData[this.articleNumberTextInputName] = new FormControl({value: currentArticleNumber, disabled: true});
    } else {
      formData[this.articleNumberTextInputName] = new FormControl({value: currentArticleNumber, disabled: this.articleNumberTextInputDisabled},
        Validators.compose([CustomValidators.trimRequired]));
    }
    if (
      !this.mcsGds221LtlExport && !this.markingSystemExport
      || (this.currentArticleNumberLong && !this.currentArticleNumberLong.isEmpty())
    ) {
      formData[this.articleNumberLongTextInputName] = new FormControl({value: this.currentArticleNumberLong.reducedArticleNumberLong || currentArticleNumber, disabled: true});
    }
    if (this.legacyExport) {
      formData[this.articleNumberTextInputLegacyName] = new FormControl(this.articleNumberLong, Validators.compose([CustomValidators.trimRequired, CustomValidators.maxLength(81)]));
    }
    formData[this.articleNumberExtensionInputName] = new FormControl({value: this.projectModel.currentProject.articleNumberExtension, disabled: articleNumberExtensionInputDisabled});
    if (this.markingSystemExport) {
      formData[this.typeClassIdComboboxName] = new FormControl({value: this.currentTypeClassId , disabled: true});
    } else {
      formData[this.typeClassIdComboboxName] = new FormControl({value: this.currentTypeClassId , disabled: this.markingSystemExport}, Validators.compose([CustomValidators.required]));
    }
    formData[this.mainNoteComboboxName] = new FormControl('', Validators.compose([CustomValidators.required]));
    if (this.mcsGds221LtlExport) {
      formData[this.productFamilyComboboxName] = new FormControl('', Validators.compose([CustomValidators.required]));
    }
    formData[this.productionPlantComboboxName] = new FormControl('', Validators.compose([CustomValidators.required]));
    if (this.isCountryCodeSelectorEnabled) {
      formData[this.countryCodeComboboxName] = new FormControl('', Validators.compose([CustomValidators.required]));
    }
    formData[this.additionalNoteComboboxName] = new FormControl([]);
    for (let i = 0; i < this.numberOfAdditionalNotes; i++) {
      formData[this.additionalNoteComboboxNames[i]] = new FormControl();
    }
    formData[this.stockSeparationComboboxName] = new FormControl();
    formData[this.changeOrderNumberInputName] = new FormControl(changeOrderNumberInputValue);
    formData[this.tariffNumberComboboxName] = new FormControl();
    formData[this.mdmGroupComboboxName] = new FormControl();

    return this.formBuilder.group(formData);
  }

  private loadAdditionalData() {
    if (this.mcsGds221LtlExport) {
      this.articleDataService.loadAllMCSArticleTypeClasses().subscribe(
        (typeClasses: ArticleTypeClassVO[]) => this.articleTypeClassLoadSuccess(typeClasses));
    } else {
      this.articleDataService.loadAllArticleTypeClasses().subscribe(
        (typeClasses: ArticleTypeClassVO[]) => this.articleTypeClassLoadSuccess(typeClasses));
    }

    this.productionPlantDataService.getAllProductionPlants().subscribe(
      (productionPlants: ProductionPlantVO[]) => this.getAllProductionPlantsSuccess(productionPlants));

    this.productionPlantDataService.getAllCountryCodes().subscribe(
      ((countryCodes: CountryCodeVO[]) => this.getAllCountryCodesSuccess(countryCodes)));

    if (!this.mcsGds221LtlExport && !this.markingSystemExport) {
      this.projectDataService.getAllTariffNumbers().subscribe(
        (tariffNumbers: CustomsTariffNumberVO[]) => this.getAllTariffNumbersSuccess(tariffNumbers));
    }
  }

  private checkForDuplicates(): void {
    this.markingSystemDataService.searchDuplicateArticleNumberForMarkingSystem(this.projectModel.currentProject).subscribe(
      (result: ProjectFinalizationInfoVO) => {
        this.checkForDuplicatesSuccess(result);
      },
      (error) => {
        this.alertHelper.showError(
          'global.title.error',
          'global.message.unexpected_error'
        );
      }
    );
  }

  private checkForDuplicatesSuccess(projectFinalizationInfoVO: ProjectFinalizationInfoVO): void {
    let articleNumber: string = null;
    if (projectFinalizationInfoVO && projectFinalizationInfoVO.duplicateArticleNumbers && projectFinalizationInfoVO.duplicateArticleNumbers.length > 0) {
      let i: number = 0;
      while (i < projectFinalizationInfoVO.duplicateArticleNumbers.length && !articleNumber) {
        if (projectFinalizationInfoVO.duplicateArticleNumbers[i]) {
            articleNumber = (projectFinalizationInfoVO.duplicateArticleNumbers[i]).articleNumber;
        }
        i++;
      }
      if (articleNumber) {
        this.articleNumberLong = new ArticleNumberLong(articleNumber);
        this.duplicateFound = true;
      }
    }
  }

  public typeClassIdComboboxChangeHandler(): void {
    this.currentTypeClassId = this.form.controls.typeClassIdCombobox.value;
    this.mainNotes = null;
    this.additionalNotes[0] = [];
    if (this.currentTypeClassId) {
      if (this.mcsGds221LtlExport) {
        this.articleDataService.loadMCSMainNotesForTypeClassId(this.currentTypeClassId).subscribe(
          ((mainNotes: {attributeId: number, displayName: string}[]) => this.loadMCSMainNotesForTypeClassIdSuccess(mainNotes))
        );
        this.articleDataService.loadMCSAdditionalNotesForTypeClassId(this.currentTypeClassId).subscribe(
          ((additionalNotes: {attributeId: number, displayName: string}[]) => this.loadMCSAdditionalNotesForTypeClassIdSuccess(additionalNotes))
        );
        this.articleDataService.loadMCSProductFamiliesForTypeClassId(this.currentTypeClassId).subscribe(
          ((productFamilies: {attributeId: number, displayName: string}[]) => this.loadMCSProductFamiliesForTypeClassIdSuccess(productFamilies))
        );
      } else {
        this.articleDataService.loadMainNotesForTypeClassId(this.currentTypeClassId).subscribe(
          ((mainNotes: {attributeId: number, displayName: string}[]) =>  this.mainNotesLoadSuccess(mainNotes))
        );
      }
    }
    this.updateSelectsWithNoSelectedValue = true;
    this.cdRef.detectChanges();
  }

  public mainNoteComboboxNameChangeHandler(): void {
    if (!this.mcsGds221LtlExport) {
      this.additionalNotes[0] = [];
      const mainNote: string = this.currentTypeClassId = this.form.controls.mainNoteCombobox.value;
      this.articleDataService.loadAdditionalNotesForMainNote(mainNote).subscribe(
        ((additionalNotes: {attributeId: number, displayName: string}[]) => this.loadAdditionalNotesForMainNoteSuccess(additionalNotes)));
      this.updateSelectsWithNoSelectedValue = true;
      this.cdRef.detectChanges();
    }
  }

  public ngOnDestroy(): void {
    this.togglePdmModal.classList.add('d-none');
  }

  public close(closeTypeSuccess: boolean = false): void {
    this.bsModalRef.hide();
    this.finish.emit(closeTypeSuccess);
  }

  public getAllProductionPlantsSuccess(productionPlantsArray: ProductionPlantVO[]): void {
    if (!productionPlantsArray) {
      return;
    }
    this.productionPlants = productionPlantsArray;
    if (this.productionPlantProjectOption !== null && this.productionPlantProjectOption.value !== null) {
      this.form.controls.productionPlantCombobox.setValue(this.productionPlantProjectOption.value);
    }
    this.updateSelectsWithNoSelectedValue = true;
    this.cdRef.detectChanges();
  }

  public getAllCountryCodesSuccess(countryCodes: CountryCodeVO[]): void {
    if (!countryCodes) {
      return;
    }
    countryCodes.sort((a: CountryCodeVO, b: CountryCodeVO) => a.sortOrder - b.sortOrder);
    let i: number = 0;
    while (i < countryCodes.length && StringUtils.isEmptyOrLiteralNull(this.interNationalCountryCode)) {
      if (countryCodes[i].codeType === CountryCodeVO.INTERNATIONAL_CODE_TYPE) {
        this.interNationalCountryCode = countryCodes[i].countryCode;
      }
      i++;
    }
    this.countryCodes = countryCodes;
    if (this.isCountryCodeSelectorEnabled && this.mcsGds221LtlExport && countryCodes.length > 0  && !StringUtils.isEmptyOrLiteralNull(this.interNationalCountryCode)) {
      this.form.controls.countryCodeCombobox.setValue([this.interNationalCountryCode]);
    }
    this.updateSelectsWithNoSelectedValue = true;
    this.cdRef.detectChanges();
  }

  public countryCodeSelectionChanged(): void {
    if (this.form.controls.countryCodeCombobox.value.length > 0) {
      this.countryCodeManuallySelected = true;
    } else {
      this.countryCodeManuallySelected = false;
    }
  }

  public updateCountryCodePreselection(): void {
    if (!this.markingSystemExport || !this.isCountryCodeSelectorEnabled) {
      return;
    }
    const select = document.getElementById('productionPlantSelect');
    const selectedIndex: number = select['selectedIndex'];
    let productionPlant: ProductionPlantVO = null;
    if (selectedIndex > -1 ) {
      productionPlant = this.productionPlants[selectedIndex];
    }
    if (this.markingSystemExport && productionPlant && this.countryCodes && !this.countryCodeManuallySelected) {
      // only use preselection if nothing is selected yet
      // if (!this.form.controls.countryCodeCombobox.value || (isArray(this.form.controls.countryCodeCombobox.value) && this.form.controls.countryCodeCombobox.value.length < 1)) {
        let i: number = 0;
        while (i < this.countryCodes.length) {
          if (this.countryCodes[i].countryCode === productionPlant.countryCode) {
            this.form.controls.countryCodeCombobox.setValue([this.countryCodes[i].countryCode]);
          }
          i++;
        }
      // }
    }
    this.cdRef.detectChanges();
  }

  public async articleTypeClassLoadSuccess(typeClasses: ArticleTypeClassVO[]): Promise<void> {
    if (!typeClasses) {
      return;
    }
    this.typeClasses = typeClasses;
    if (!this.currentTypeClassId) {
      this.currentTypeClassId = this.projectModel.currentProject.typeClassId;
    }
    if (!this.currentTypeClassId) {
      this.currentTypeClassId = this.projectModel.currentProject.typeClassIdTemp;
    }
    if (!this.currentTypeClassId && this.markingSystemExport && !this.fNumber) {
      this.currentTypeClassId = this.markingSystemTypeClassId;
    }
    if (this.currentTypeClassId) {
      this.form.controls.typeClassIdCombobox.setValue(this.currentTypeClassId);
    }

    if (!this.legacyExport && !this.mcsGds221LtlExport && !this.markingSystemExport) {
      const preselectionData = await this.getPreselectionData();
      const oldData = preselectionData[0];
      const newData = preselectionData[1];

      oldData.articleNumber = newData.articleNumber;

      if (this.choosePreselectionWindowShouldBeOpened(oldData, newData)) {
        this.projectComponentPresenter.openChooseFinalizationPreselectionDialog(oldData, newData).then((useNewValue: boolean) => {
          const value: FinalizationPreselectionVO = useNewValue ? newData : oldData;
          this.handleFinalizationPreselectionResult(value, true);
        });
      } else {
        this.handleFinalizationPreselectionResult(newData, true);
      }

    } else {
      this.handleLoadingAndPreselectionOfAdditionalProjectData();
    }

    this.updateSelectsWithNoSelectedValue = true;
    this.cdRef.detectChanges();
  }

  public choosePreselectionWindowShouldBeOpened(oldData: FinalizationPreselectionVO, newData: FinalizationPreselectionVO): boolean {
    return oldData && newData && ((oldData.classId && oldData.classId !== newData.classId) ||
        (oldData.mainNote && oldData.mainNote !== newData.mainNote) ||
        (oldData.customsTariffNumber && oldData.customsTariffNumber !== newData.customsTariffNumber));
  }

  private handleFinalizationPreselectionResult(result: FinalizationPreselectionVO, searchAdditionalData: boolean): void {
    if (!result) {
      return;
    }

    if (!this.form.controls.articleNumberLongTextInput.value) {
      this.updateArticleNumberLongTextInput(new ArticleNumberLong(result.articleNumber).reducedArticleNumberLong);
    }

    if (searchAdditionalData) {
      const finalizationInfo = new ProjectFinalizationInfoVO();
      finalizationInfo.mainNote = result.mainNote;
      finalizationInfo.customsTariffNumber = result.customsTariffNumber;
      this.projectFinalizationInfoVO = finalizationInfo;

      if (result.customsTariffNumber) {
        this.preselectTariffNumber();
      }

      if (result.classId && this.typeClasses && this.typeClasses.some(typeClass => result.classId === typeClass.classId)) {
        this.form.controls.typeClassIdCombobox.setValue(result.classId);
        this.preselectMainNote = true;
        this.typeClassIdComboboxChangeHandler();
      }
    }
  }

  public ngAfterViewChecked(): void {
    if (!this.updateSelectsWithNoSelectedValue || !this.form || !this.form.controls) {
      return;
    }
    const selectIdToControlName: Object = {};
    selectIdToControlName['typeClassIdSelect'] = this.typeClassIdComboboxName;
    selectIdToControlName['mainNoteSelect'] = this.mainNoteComboboxName;
    selectIdToControlName['productFamilySelect'] = this.productFamilyComboboxName;
    selectIdToControlName['additonalNoteSelect'] = this.additionalNoteComboboxName;
    for (let i = 0; i < this.numberOfAdditionalNotes; i++) {
      selectIdToControlName[`additionalNote${(i + 1).toString().padStart(2, "0")}Select`] = this.additionalNoteComboboxNames[i];
    }
    selectIdToControlName['productionPlantSelect'] = this.productionPlantComboboxName;
    selectIdToControlName['countryCodeSelect'] = this.countryCodeComboboxName;
    Object.keys(selectIdToControlName).forEach((key) => {
      const select = document.getElementById(key);
      if (select) {
        const controlName: string = selectIdToControlName[key];
        if (this.form.controls[controlName]) {
          if (!this.form.controls[controlName].value) {
            select['selectedIndex'] = -1;
          }
          this.form.controls[controlName].updateValueAndValidity();
        }
      }
    });
    this.updateSelectsWithNoSelectedValue = false;
  }

  private async getPreselectionData(): Promise<FinalizationPreselectionVO[]> {
    const additionalDataForPreselection = await lastValueFrom(this.getAdditionalProjectDataForPreselection());
    const finalizationPreselection =
      await lastValueFrom(
        this.projectDataService.getFinalizationPreselection(this.projectModel.currentProject.id, this.projectModel.currentProject.currentVersion, true)
      );
    return [additionalDataForPreselection, finalizationPreselection];
  }

  private getAdditionalProjectDataForPreselection(): Observable<FinalizationPreselectionVO> {
    return this.projectDataService.getAdditionalProjectData(this.projectModel.currentProject.id).pipe(
      map((result: ProjectFinalizationInfoVO) => {
        this.preselectMainNote = true;
          const resultForPreselection: FinalizationPreselectionVO = FinalizationPreselectionVO.create(
            this.currentTypeClassId,
            result.mainNote,
            result.customsTariffNumber);
          return resultForPreselection;
      }));
  }

  private handleLoadingAndPreselectionOfAdditionalProjectData(): void {
    this.form.controls.articleNumberTextInput.updateValueAndValidity();
    this.form.controls.typeClassIdCombobox.updateValueAndValidity();
    this.updateSelectsWithNoSelectedValue = true;
    this.cdRef.detectChanges();
    if (this.markingSystemExport && !this.fNumber) {
      const articleNumber: string = this.getMarkingSystemArticleNumber();
      const seriesString: string = articleNumber.split('-')[0];
      const seriesNumber: number = parseFloat(seriesString);
      this.markingSystemDataService.loadFinalizationInfoForSeriesNumber(seriesNumber).subscribe(
        (result: ProjectFinalizationInfoVO) => {
          this.projectFinalizationInfoVO = result;
          this.preselectMainNote = true;
          this.typeClassIdComboboxChangeHandler();
        }
      );
    } else {
      this.projectDataService.getAdditionalProjectData(this.projectModel.currentProject.id).subscribe(
        (result: ProjectFinalizationInfoVO) => {
          this.projectFinalizationInfoVO = result;
          this.preselectMainNote = true;
          if (this.mcsGds221LtlExport) {
            this.preselectProductFamily = true;
            this.preselectAdditionalNotes = true;
          }
          this.preselectTariffNumber();
          this.typeClassIdComboboxChangeHandler();
        }
      );
    }
  }

  public mainNotesLoadSuccess(mainNotes: {attributeId: number, displayName: string}[]): void {
    if (!mainNotes) {
      return;
    }
    this.mainNotes = mainNotes;
    if (this.projectFinalizationInfoVO != null && this.preselectMainNote) {
      this.preselectMainNote = false;
      if (this.projectFinalizationInfoVO.mainNote && this.mainNotes && this.mainNotes.some(mainNote => this.projectFinalizationInfoVO.mainNote === mainNote['attributeId'])) {
        this.form.controls.mainNoteCombobox.setValue(this.projectFinalizationInfoVO.mainNote);
        this.mainNoteComboboxNameChangeHandler();
      }
    } else {
      this.form.controls.mainNoteCombobox.setValue('');
    }
    this.updateSelectsWithNoSelectedValue = true;
    this.cdRef.detectChanges();
  }

  public loadAdditionalNotesForMainNoteSuccess(additionalNotes: {attributeId: number, displayName: string}[]): void {
    if (!additionalNotes) {
      return;
    }
    this.additionalNotes[0] = additionalNotes;
    if (this.projectFinalizationInfoVO != null && this.preselectAdditionalNotes) {
      this.preselectAdditionalNotes = false;
      const newValue = [];
      if (this.projectFinalizationInfoVO.additionalNote01) {
        newValue.push(this.projectFinalizationInfoVO.additionalNote01);
      }
      if (this.projectFinalizationInfoVO.additionalNote02) {
        newValue.push(this.projectFinalizationInfoVO.additionalNote02);
      }
      if (this.projectFinalizationInfoVO.additionalNote03) {
        newValue.push(this.projectFinalizationInfoVO.additionalNote03);
      }
      if (this.projectFinalizationInfoVO.additionalNote04) {
        newValue.push(this.projectFinalizationInfoVO.additionalNote04);
      }
      if (this.projectFinalizationInfoVO.additionalNote05) {
        newValue.push(this.projectFinalizationInfoVO.additionalNote05);
      }
      if (this.projectFinalizationInfoVO.additionalNote06) {
        newValue.push(this.projectFinalizationInfoVO.additionalNote06);
      }
      if (this.projectFinalizationInfoVO.additionalNote07) {
        newValue.push(this.projectFinalizationInfoVO.additionalNote07);
      }
      if (this.projectFinalizationInfoVO.additionalNote08) {
        newValue.push(this.projectFinalizationInfoVO.additionalNote08);
      }
      if (this.projectFinalizationInfoVO.additionalNote09) {
        newValue.push(this.projectFinalizationInfoVO.additionalNote09);
      }
      if (this.projectFinalizationInfoVO.additionalNote10) {
        newValue.push(this.projectFinalizationInfoVO.additionalNote10);
      }
      if (this.projectFinalizationInfoVO.additionalNote11) {
        newValue.push(this.projectFinalizationInfoVO.additionalNote11);
      }
      if (this.projectFinalizationInfoVO.additionalNote12) {
        newValue.push(this.projectFinalizationInfoVO.additionalNote12);
      }
      this.form.controls.additionalNoteCombobox.setValue(newValue);
    } else {
      this.form.controls.additionalNoteCombobox.setValue([]);
    }
    this.updateSelectsWithNoSelectedValue = true;
    this.cdRef.detectChanges();
  }

  public loadMCSMainNotesForTypeClassIdSuccess(mainNotes: {attributeId: number, displayName: string}[]): void {
    if (!mainNotes) {
      return;
    }
    this.mainNotes = mainNotes;
    if (this.projectFinalizationInfoVO != null && this.preselectMainNote) {
      this.preselectMainNote = false;
      this.form.controls.mainNoteCombobox.setValue(this.projectFinalizationInfoVO.mainNote);
      this.mainNoteComboboxNameChangeHandler();
    } else {
      this.form.controls.mainNoteCombobox.setValue('');
    }
    this.updateSelectsWithNoSelectedValue = true;
    this.cdRef.detectChanges();
  }

  public loadMCSAdditionalNotesForTypeClassIdSuccess(additionalNotes: {attributeId: number, displayName: string}[]): void {
    if (!additionalNotes) {
      return;
    }
    // eslint-disable-next-line prefer-const
    let dataproviders: {attributeId: number, displayName: string}[][] = [];
    let i: number = 1;
    while (i < 13) {
      dataproviders[i] = [];
      i++;
    }
    additionalNotes.forEach(function(value) {
      const orderSlot = value['orderSlot'];
      if (orderSlot && orderSlot > 0 && orderSlot < 13) {
        if (dataproviders[orderSlot].length < 1) {
          const emptyValue = {displayName: '', attributeId: -1};
          dataproviders[orderSlot].push(emptyValue);
        }
        dataproviders[orderSlot].push(value);
      }
    });

    for (let i = 1; i <= this.numberOfAdditionalNotes; i++) {
      this.additionalNotes[i] = dataproviders[i];
    }
    if (this.projectFinalizationInfoVO != null && this.preselectAdditionalNotes) {
      this.form.controls.additionalNoteCombobox01.setValue(this.projectFinalizationInfoVO.additionalNote01);
      this.form.controls.additionalNoteCombobox02.setValue(this.projectFinalizationInfoVO.additionalNote02);
      this.form.controls.additionalNoteCombobox03.setValue(this.projectFinalizationInfoVO.additionalNote03);
      this.form.controls.additionalNoteCombobox04.setValue(this.projectFinalizationInfoVO.additionalNote04);
      this.form.controls.additionalNoteCombobox05.setValue(this.projectFinalizationInfoVO.additionalNote05);
      this.form.controls.additionalNoteCombobox06.setValue(this.projectFinalizationInfoVO.additionalNote06);
      this.form.controls.additionalNoteCombobox07.setValue(this.projectFinalizationInfoVO.additionalNote07);
      this.form.controls.additionalNoteCombobox08.setValue(this.projectFinalizationInfoVO.additionalNote08);
      this.form.controls.additionalNoteCombobox09.setValue(this.projectFinalizationInfoVO.additionalNote09);
      this.form.controls.additionalNoteCombobox10.setValue(this.projectFinalizationInfoVO.additionalNote10);
      this.form.controls.additionalNoteCombobox11.setValue(this.projectFinalizationInfoVO.additionalNote11);
      this.form.controls.additionalNoteCombobox12.setValue(this.projectFinalizationInfoVO.additionalNote12);
      this.preselectAdditionalNotes = false;
    } else {
      this.form.controls.additionalNoteCombobox01.setValue('');
      this.form.controls.additionalNoteCombobox02.setValue('');
      this.form.controls.additionalNoteCombobox03.setValue('');
      this.form.controls.additionalNoteCombobox04.setValue('');
      this.form.controls.additionalNoteCombobox05.setValue('');
      this.form.controls.additionalNoteCombobox06.setValue('');
      this.form.controls.additionalNoteCombobox07.setValue('');
      this.form.controls.additionalNoteCombobox08.setValue('');
      this.form.controls.additionalNoteCombobox09.setValue('');
      this.form.controls.additionalNoteCombobox10.setValue('');
      this.form.controls.additionalNoteCombobox11.setValue('');
      this.form.controls.additionalNoteCombobox12.setValue('');
    }
    this.updateSelectsWithNoSelectedValue = true;
    this.cdRef.detectChanges();
  }

  public loadMCSProductFamiliesForTypeClassIdSuccess(productfamilies: {attributeId: number, displayName: string}[]): void {
    if (!productfamilies) {
      return;
    }
    this.productFamilies = productfamilies;
    if (this.projectFinalizationInfoVO != null && this.preselectProductFamily) {
      this.preselectProductFamily = false;
      this.form.controls.productFamilyCombobox.setValue(this.projectFinalizationInfoVO.productFamily);
    } else {
      this.form.controls.productFamilyCombobox.setValue('');
    }
    this.updateSelectsWithNoSelectedValue = true;
    this.cdRef.detectChanges();
  }

  private getAllTariffNumbersSuccess(tariffNumbers: CustomsTariffNumberVO[]): void {
    tariffNumbers.sort((a: CustomsTariffNumberVO, b: CustomsTariffNumberVO) => a.sortOrder - b.sortOrder);
    this.tariffNumbers = [CustomsTariffNumberVO.create(''), ...tariffNumbers];
    this.preselectTariffNumber();
  }

  private preselectTariffNumber(): void {
    if (!this.tariffNumbers) {
      return;
    }

    let value: string = '';
    if (this.projectFinalizationInfoVO) {
      const preselectedTariffNumber: string = this.projectFinalizationInfoVO.customsTariffNumber;
      if (preselectedTariffNumber && this.tariffNumbers.some(number => preselectedTariffNumber === number.value)) {
        value = preselectedTariffNumber;
      }
    }
    this.form.controls.tariffNumberCombobox.setValue(value);

    this.cdRef.detectChanges();
  }

  public getProductionPlantLabel(item: ProductionPlantVO): string {
    if (item) {
      return item.plantNo + ' ' + item.name;
    }
    return '';
  }

  public getCountryCodeLabel(item: CountryCodeVO): string {
    if (item) {
      return item.countryCode + ' ' + item.displayName;
    }
    return '';
  }

  public finalizeButtonClickHandler(): void {
    if (!this.validateForm()) {
      return;
    }
    if (!this.markingSystemExport && !this.useCustomArticleNumber && !this.legacyExport) {
      if (this.form.controls.articleNumberTextInput && this.validateArticleNumber(this.form.controls.articleNumberTextInput.value)) {
        this.form.controls.articleNumberTextInput.setValue(this.createPaddedArticleNumber(this.form.controls.articleNumberTextInput.value));
      }
      if (this.form.controls.articleNumberExtensionInput && this.validateArticleNumber(this.form.controls.articleNumberExtensionInput.value)) {
        if (!StringUtils.isEmptyOrLiteralNull(this.form.controls.articleNumberExtensionInput.value)) {
          this.form.controls.articleNumberExtensionInput.setValue(this.createPaddedArticleNumber(this.form.controls.articleNumberExtensionInput.value));
        }
      }
    }
    const finInfo: ProjectFinalizationInfoVO = new ProjectFinalizationInfoVO();
    finInfo.mainNote = this.form.controls.mainNoteCombobox.value;
    finInfo.customsTariffNumber = this.form.controls.tariffNumberCombobox.value;
    finInfo.mdmGroup = this.form.controls.mdmGroupCombobox.value ?? this.userModel.user.mdmGroup;
    if (!this.mcsGds221LtlExport && this.form.controls.additionalNoteCombobox.value && this.form.controls.additionalNoteCombobox.value.length > 0) {
      if (this.form.controls.additionalNoteCombobox.value[0]) {
        finInfo.additionalNote01 = this.form.controls.additionalNoteCombobox.value[0];
      }
      if (this.form.controls.additionalNoteCombobox.value[1]) {
        finInfo.additionalNote02 = this.form.controls.additionalNoteCombobox.value[1];
      }
      if (this.form.controls.additionalNoteCombobox.value[2]) {
        finInfo.additionalNote03 = this.form.controls.additionalNoteCombobox.value[2];
      }
      if (this.form.controls.additionalNoteCombobox.value[3]) {
        finInfo.additionalNote04 = this.form.controls.additionalNoteCombobox.value[3];
      }
      if (this.form.controls.additionalNoteCombobox.value[4]) {
        finInfo.additionalNote05 = this.form.controls.additionalNoteCombobox.value[4];
      }
      if (this.form.controls.additionalNoteCombobox.value[5]) {
        finInfo.additionalNote06 = this.form.controls.additionalNoteCombobox.value[5];
      }
      if (this.form.controls.additionalNoteCombobox.value[6]) {
        finInfo.additionalNote07 = this.form.controls.additionalNoteCombobox.value[6];
      }
      if (this.form.controls.additionalNoteCombobox.value[7]) {
        finInfo.additionalNote08 = this.form.controls.additionalNoteCombobox.value[7];
      }
      if (this.form.controls.additionalNoteCombobox.value[8]) {
        finInfo.additionalNote09 = this.form.controls.additionalNoteCombobox.value[8];
      }
      if (this.form.controls.additionalNoteCombobox.value[9]) {
        finInfo.additionalNote10 = this.form.controls.additionalNoteCombobox.value[9];
      }
      if (this.form.controls.additionalNoteCombobox.value[10]) {
        finInfo.additionalNote11 = this.form.controls.additionalNoteCombobox.value[10];
      }
      if (this.form.controls.additionalNoteCombobox.value[11]) {
        finInfo.additionalNote12 = this.form.controls.additionalNoteCombobox.value[11];
      }
    } else if (this.mcsGds221LtlExport) {
      if (this.form.controls.additionalNoteCombobox01.value) {
        finInfo.additionalNote01 = this.form.controls.additionalNoteCombobox01.value;
      }
      if (this.form.controls.additionalNoteCombobox02.value) {
        finInfo.additionalNote02 = this.form.controls.additionalNoteCombobox02.value;
      }
      if (this.form.controls.additionalNoteCombobox03.value) {
        finInfo.additionalNote03 = this.form.controls.additionalNoteCombobox03.value;
      }
      if (this.form.controls.additionalNoteCombobox04.value) {
        finInfo.additionalNote04 = this.form.controls.additionalNoteCombobox04.value;
      }
      if (this.form.controls.additionalNoteCombobox05.value) {
        finInfo.additionalNote05 = this.form.controls.additionalNoteCombobox05.value;
      }
      if (this.form.controls.additionalNoteCombobox06.value) {
        finInfo.additionalNote06 = this.form.controls.additionalNoteCombobox06.value;
      }
      if (this.form.controls.additionalNoteCombobox07.value) {
        finInfo.additionalNote07 = this.form.controls.additionalNoteCombobox07.value;
      }
      if (this.form.controls.additionalNoteCombobox08.value) {
        finInfo.additionalNote08 = this.form.controls.additionalNoteCombobox08.value;
      }
      if (this.form.controls.additionalNoteCombobox09.value) {
        finInfo.additionalNote09 = this.form.controls.additionalNoteCombobox09.value;
      }
      if (this.form.controls.additionalNoteCombobox10.value) {
        finInfo.additionalNote10 = this.form.controls.additionalNoteCombobox10.value;
      }
      if (this.form.controls.additionalNoteCombobox11.value) {
        finInfo.additionalNote11 = this.form.controls.additionalNoteCombobox11.value;
      }
      if (this.form.controls.additionalNoteCombobox12.value) {
        finInfo.additionalNote12 = this.form.controls.additionalNoteCombobox12.value;
      }
      if (this.form.controls.productFamilyCombobox) {
        finInfo.productFamily = this.form.controls.productFamilyCombobox.value;
      }
    }
    if (this.form.controls.productionPlantCombobox.value) {
      if (this.productionPlantProjectOption) {
        this.productionPlantProjectOption.value = this.form.controls.productionPlantCombobox.value;
      } else {
        if (!this.projectModel.currentProject.options) {
          this.projectModel.currentProject.options = [];
        }
        this.productionPlantProjectOption = ProjectOptionUtils.convertProjectOptionDefToProjectOption(ProjectOptionUtils.findProjectOptionDefByIdentifier(this.projectUserModel.projectOptionDefs, ProjectOptionDefIds.PRODUCTION_PLANT));
        if (this.productionPlantProjectOption == null) {
          this.alertHelper.showError('finalizeProject.title.production_plant_project_option_not_found', 'finalizeProject.message.production_plant_project_option_not_found');
          return;
        }
        this.productionPlantProjectOption.value = this.form.controls.productionPlantCombobox.value;
        this.projectModel.currentProject.options.push(this.productionPlantProjectOption);
      }
    }
    this.projectModel.currentProject.typeClassIdTemp = this.form.controls.typeClassIdCombobox.value;
    this.projectModel.currentProject.articleNumber = this.form.controls.articleNumberTextInput.value;
    if (this.form.controls.articleNumberLongTextInput?.value) {
      this.projectModel.currentProject.articleNumberLong.extendedArticleNumberLong = this.form.controls.articleNumberLongTextInput.value;
    }
    this.projectModel.currentProject.articleNumberExtension = this.form.controls.articleNumberExtensionInput.value;
    if (this.form.controls.stockSeparationCombobox.value || this.stockSeparationProjectOption) {
      if (this.stockSeparationProjectOption != null) {
        if (this.form.controls.stockSeparationCombobox.value) {
          this.stockSeparationProjectOption.value = this.form.controls.stockSeparationCombobox.value;
        } else {
          this.stockSeparationProjectOption.value = '';
        }
      } else {
        if (this.form.controls.stockSeparationCombobox.value) {
          this.stockSeparationProjectOption = ProjectOptionUtils.convertProjectOptionDefToProjectOption(ProjectOptionUtils.findProjectOptionDefByIdentifier(this.projectUserModel.projectOptionDefs, ProjectOptionDefIds.STOCK_SEPARATION));
          this.stockSeparationProjectOption.value = this.form.controls.stockSeparationCombobox.value;
          this.projectModel.currentProject.options.push(this.stockSeparationProjectOption);
        }
      }
    }
    if (this.changeOrderNumberProjectOption == null) {
      this.changeOrderNumberProjectOption = ProjectOptionUtils.convertProjectOptionDefToProjectOption(ProjectOptionUtils.findProjectOptionDefByIdentifier(this.projectUserModel.projectOptionDefs, ProjectOptionDefIds.CO_NUMBER));
      this.projectModel.currentProject.options.push(this.changeOrderNumberProjectOption);
    }
    if (this.changeOrderNumberProjectOption != null) {
      this.changeOrderNumberProjectOption.value = this.form.controls.changeOrderNumberInput.value;
    }
    this.projectDataService.saveAdditionalProjectData(this.projectModel.currentProject.id, finInfo).subscribe(
      () =>  this.continueFinalizing(),
      () => this.saveAdditionalProjectDataError()
    );
  }

  private saveAdditionalProjectDataError(): void {
    this.alertHelper.showError('global.title.error', 'global.message.unexpected_error');
  }

  public continueFinalizing(): void {
    if (this.projectModel && this.markingSystemExport && (this.fNumber == null || this.fNumber.length < 1)) {
      this.continueMarkingSystemFinalization();
    } else {
      this.continueRegularFinalization();
    }
    this.close();
  }

  private continueRegularFinalization(): void {
    const typeClassId: string = this.form.controls.typeClassIdCombobox.value;
    let articleNumber: string = this.articleNumber;
    let drawingNumber: string = this.drawingNumber;
    let articleNumberLong: ArticleNumberLong;
    let articleNumberExtension: string = this.articleNumberExtension;
    if (this.legacyExport) {
      articleNumberLong = new ArticleNumberLong(this.form.controls.articleNumberTextInputLegacy.value);
    } else {
      this.storeMcsGdsInitArticleNumberIfNeeded();
      articleNumber = this.form.controls.articleNumberTextInput.value;
      drawingNumber = this.drawingNumber;
      articleNumberLong = this.calculatedArticleNumberLong;
      articleNumberExtension = this.form.controls.articleNumberExtensionInput.value;
      if (this.useCustomArticleNumber && (this.mcsGds221LtlExport || IsProjectType.visibleMountingRail(this.projectModel.currentProject) || IsProjectType.freeMounting(this.projectModel.currentProject))) {
        articleNumber = articleNumberLong.reducedArticleNumberLong;
        drawingNumber = '';
      }
      if (
        (this.fNumber != null && this.fNumber.length > 0)
        || this.isArticleNumberGenerated
        || (this.currentArticleNumberLong && !this.currentArticleNumberLong.isEmpty())
      ) {
        // reuse existing values in case of a repeated export or in case article numbers were automatically generated prior to pdm export (at project request)
        articleNumber = this.projectModel.currentProject.articleNumber;
        drawingNumber = this.projectModel.currentProject.drawingNumber;
        articleNumberLong = this.projectModel.currentProject.articleNumberLong;
        articleNumberExtension = this.projectModel.currentProject.articleNumberExtension;
      }
    }
    const countryCodeArray: string[] = this.getSanitizedCountryCodeArray();
    this.saveCountryCodeArray(countryCodeArray);
    const countryCodeString: string = countryCodeArray.join(',');
    const mdmGroup: MdmGroupVO = this.form.controls.mdmGroupCombobox.value ?? this.userModel.user.mdmGroup;
    this.projectComponentPresenter.openEditConfigurationSettingsComponent({
      legacyFinalization: this.legacyExport,
      documentVersion: this.documentVersion,
      articleNumber: articleNumber,
      articleNumberLong: articleNumberLong,
      drawingNumber: drawingNumber,
      articleNumberExtension: articleNumberExtension,
      typeClassId: typeClassId,
      version: this.projectModel.currentProject.currentVersion,
      fNumber: this.fNumber,
      countryCodeString: countryCodeString,
      useCustomArticleNumber: this.useCustomArticleNumber,
      tcVersion: this.legacyExport ? this.tcVersion : -1,
      onlyDrawing: this.legacyExport ? this.onlyDrawing : false,
      mdmGroup: mdmGroup,
      projectOptionsEditable: true,
      saveAndFinalizeProject: true,
      allowEditOfOptionLabels: false
    });
  }

  private storeMcsGdsInitArticleNumberIfNeeded(): void {
    if (this.mcsGds221LtlExport && !this.useCustomArticleNumber) {
      const currentInitArticleNumber: string = this.projectModel.currentInfoProperties.getString("mcsGdsInitArticleNo");
      const inputArticleNumber = StringUtils.trim(this.form.controls.articleNumberTextInput.value);
      if (inputArticleNumber !== currentInitArticleNumber) {
        const inputArticleNumberPVO: PropertyValueVO = new PropertyValueVO();
        inputArticleNumberPVO.type = PropertyValueType.STRING;
        inputArticleNumberPVO.stringValue = inputArticleNumber;
        this.projectModel.currentInfoProperties.setMultiple("mcsGdsInitArticleNo", [inputArticleNumberPVO], false, true, false);
      }
      this.updateArticleNumberLong();
    }
  }

  public updateArticleNumberLong(generateArticleNumber: boolean = null): void {
    if (this.legacyExport) {
      return;
    }
    if (generateArticleNumber !== null) {
      this.useCustomArticleNumber = !generateArticleNumber;
      if (!this.useCustomArticleNumber) {
        if (StringUtils.isEmpty(this.form.controls[this.articleNumberTextInputName].value)) {
          if (this.mcsGds221LtlExport) {
            const mcsGdsArticleNumber: string = this.projectModel.currentInfoProperties.getString("mcsGdsInitArticleNo");
            this.form.controls[this.articleNumberTextInputName].setValue(StringUtils.isEmpty(mcsGdsArticleNumber) ? '' : mcsGdsArticleNumber);
          } else {
            this.form.controls[this.articleNumberTextInputName].setValue('');
          }
        }
      }
    }
    if (this.useCustomArticleNumber) {
      if (this.form.controls.articleNumberExtensionInput.value) {
        this.calculatedArticleNumberLong = new ArticleNumberLong(this.form.controls.articleNumberTextInput.value + '/' + this.form.controls.articleNumberExtensionInput.value);
      } else {
        this.calculatedArticleNumberLong = new ArticleNumberLong(this.form.controls.articleNumberTextInput.value);
      }
      this.drawingNumber = '';
      return;
    }
    if (this.validateArticleNumber(this.form.controls.articleNumberTextInput.value)) {
      this.form.controls[this.articleNumberTextInputName].setErrors(null);
    }
    if (this.validateArticleNumber(this.form.controls.articleNumberExtensionInput.value)) {
      this.form.controls[this.articleNumberExtensionInputName].setErrors(null);
    }
    const articleNumberLong: string = this.createPaddedArticleNumber(this.form.controls.articleNumberTextInput.value);
    let drawingNumber: string = this.drawingNumber;
    if (!drawingNumber) {
      drawingNumber = '';
    }
    while (drawingNumber.length < 7) {
      drawingNumber = '0' + drawingNumber;
    }
    drawingNumber = drawingNumber.slice(0, 3) + '-' + drawingNumber.slice(3, 7);
    this.calculatedArticleNumberLong = new ArticleNumberLong(articleNumberLong + '/Z' + drawingNumber);
    if (this.form.controls.articleNumberExtensionInput.value) {
      const articleNumberExtension: string = this.createPaddedArticleNumber(this.form.controls.articleNumberTextInput.value);
      this.calculatedArticleNumberLong = new ArticleNumberLong(this.calculatedArticleNumberLong.reducedArticleNumberLong + '/' + articleNumberExtension);
    }
    this.form.controls[this.articleNumberTextInputName].updateValueAndValidity();
    this.form.controls[this.articleNumberExtensionInputName].updateValueAndValidity();
  }

  public isArticleNoEditable(): boolean {
    return UserUtils.hasFinalizeProjectsPermission(this.userModel.user)
      && !(ProjectUtils.isProjectFinalized(this.projectModel.currentProject.finalizationState));
  }

  private validateArticleNumber(articleNumber: string): boolean {
    if (this.legacyExport || !this.useCustomArticleNumber) {
      return true;
    }
    if (StringUtils.isEmptyOrLiteralNull(articleNumber)) {
      return false;
    }
    if (this.articleNumber.length < 6 || (this.articleNumber.length > 9 || this.mcsGds221LtlExport)) {
      return false;
    }
    const firstDashIndex: number = articleNumber.indexOf('-');
    if (firstDashIndex > -1 && firstDashIndex < articleNumber.length) {
      if (articleNumber.indexOf('_', firstDashIndex + 1) > -1) {
        return false;
      }
    } else {
      return false;
    }
    if (firstDashIndex < 3 || firstDashIndex > 4) {
      return false;
    }
    if (firstDashIndex === 3 && articleNumber.length > 8) {
      return false;
    }
    return true;
  }

  private createPaddedArticleNumber(articleNumber: string): string {
    let newArticleNumber: string = '';
    let characterPosition: number = 0;
    let currentBlock: string = '';
    while (characterPosition < articleNumber.length) {
      const currentCharacter: string = articleNumber.substr(characterPosition, 1);
      if (currentCharacter === '-' || currentCharacter === '/') {
        newArticleNumber = newArticleNumber + this.paddWithLeadingZeroes(currentBlock) + currentCharacter;
        currentBlock = '';
      } else {
        currentBlock = currentBlock + currentCharacter;
      }
      characterPosition++;
    }
    newArticleNumber += this.paddWithLeadingZeroes(currentBlock);
    return newArticleNumber;
  }

  private paddWithLeadingZeroes(inputString: string, targetLength: number = 4): string {
    if (inputString == null) {
      inputString = '';
    }
    while (inputString.length < targetLength) {
      inputString = '0' + inputString;
    }
    return inputString;
  }


  private getSanitizedCountryCodeArray(): string[] {
    const result: string[] = [];
    if (this.fNumber && this.fNumber.length >= 1) {
      // if this is not the first  finalization then the previous countryCode will be used
      return this.projectModel.currentInfoProperties.getStrings("countryCode");
    } else {
      if (isArray(this.countryCodes) && this.countryCodes.length > 0 && !StringUtils.isEmptyOrLiteralNull(this.interNationalCountryCode)) {
        if (!this.isCountryCodeSelectorEnabled) {
          result.push(this.interNationalCountryCode);
        } else {
          // due to earlier validation we already know this is an array of length > 0
          const selectedCCs: string[] = this.form.controls.countryCodeCombobox.value;
          // if countryCode International is among the selected then only that will be used and all other selected countryCodes are ignored as "International" covers all
          if (selectedCCs.indexOf(this.interNationalCountryCode) > -1) {
            result.push(this.interNationalCountryCode);
          } else {
            return selectedCCs;
          }
        }
      }
    }
    return result;
  }

  private saveCountryCodeArray(ccArray: string[]): void {
    this.projectModel.currentInfoProperties.setStrings("countryCode", ccArray);
  }

  private continueMarkingSystemFinalization(): void {
    const countryCodeArray: string[] = this.getSanitizedCountryCodeArray();
    this.saveCountryCodeArray(countryCodeArray);
    const countryCodeString: string = countryCodeArray.join(',');
    const articleNumber: string = this.getMarkingSystemArticleNumber();
    const drawingNumber: string = '';
    const articleNumberLong: ArticleNumberLong = this.duplicateFound ? this.articleNumberLong : new ArticleNumberLong(articleNumber + '/Z');
    const useCustomArticleNumber = this.duplicateFound || this.useCustomArticleNumber;
    const typeClassId: string = this.markingSystemTypeClassId;
    const mdmGroup: MdmGroupVO = this.form.controls.mdmGroupCombobox.value;
    this.projectComponentPresenter.openEditConfigurationSettingsComponent({
      articleNumber: articleNumber,
      articleNumberLong: articleNumberLong,
      drawingNumber: drawingNumber,
      typeClassId: typeClassId,
      version: this.projectModel.currentProject.currentVersion,
      fNumber: this.fNumber,
      countryCodeString: countryCodeString,
      useCustomArticleNumber: useCustomArticleNumber,
      tcVersion: -1,
      onlyDrawing: false,
      mdmGroup: mdmGroup,
      projectOptionsEditable: true,
      saveAndFinalizeProject: true,
      allowEditOfOptionLabels: false
    });
    this.close();
  }

  private getCurrentArticleNumber(fnumber: string, legacyExport: boolean, articleNumber: string, currentProjectArticleNumber: string, currentProjectType: ProjectType, isArticleNumberGenerated: boolean): string {
    if (legacyExport) {
      return articleNumber;
    }
    if (IsProjectType.visibleMountingRail(currentProjectType) || IsProjectType.freeMounting(currentProjectType)) {
      if (!StringUtils.isEmpty(fnumber) && currentProjectArticleNumber != null && currentProjectArticleNumber.length > 9) {
        // this.maxArticleNumberChars = 80;
        this.useCustomArticleNumber = true;
      } else if (isArticleNumberGenerated) {
        this.preselectGenerateArticleNumberElement = false;
        this.useCustomArticleNumber = true;
      } else {
        this.useCustomArticleNumber = false;
        this.preselectGenerateArticleNumberElement = true;
      }
    } else if (isArticleNumberGenerated) {
      this.useCustomArticleNumber = true;
      return currentProjectArticleNumber;
    }
    if (!StringUtils.isEmpty(currentProjectArticleNumber)) {
      return currentProjectArticleNumber;
    }
    return this.getArticleNumberByProjectType(currentProjectType);
  }

  private getArticleNumberByProjectType(currentProjectType: ProjectType): string {
    switch(currentProjectType) {
      case "MARKINGSYSTEM": return this.getMarkingSystemArticleNumber();

      case "MCS":
      case "GDS":
      case "SPLICING_CONNECTOR_221": {
        const mcsGdsArticleNumber: string = this.projectModel.currentInfoProperties.getString("mcsGdsInitArticleNo");
        return StringUtils.isEmpty(mcsGdsArticleNumber) ? '' : mcsGdsArticleNumber;
      }

      case "LTL": {
        const baseArticleNumber: string = this.projectModel?.baseArticle.articleNo;
        return StringUtils.isEmpty(baseArticleNumber) ? '' : baseArticleNumber;
      }

      default: return '';
    }
  }

  private getMarkingSystemArticleNumber(): string {
    let article: ArticleVO = this.projectService.getFirstArticle(this.projectModel.currentProject);
    return article.articleNo;
  }

  public editArticleNumber(): void {
    this.projectComponentPresenter.openEditProjectArticleNumberPopup(
      this.form.controls.articleNumberLongTextInput.value,
      this.showGenerateArticleNumberElement,
      this.preselectGenerateArticleNumberElement
    ).then(result => {
      if (result) {
        const updatedArticleNumber: string = new ArticleNumberLong(result.articleNumber).reducedArticleNumberLong;
        this.form.controls.articleNumberLongTextInput.setValue(updatedArticleNumber);
        this.form.controls.articleNumberTextInput.setValue(updatedArticleNumber);
        this.preselectGenerateArticleNumberElement = result.isAutomaticGenerationSelected;
        this.updateArticleNumberLongTextInput(updatedArticleNumber);
        this.updateArticleNumberLong(this.preselectGenerateArticleNumberElement);
      }
    });
  }

  private updateArticleNumberLongTextInput(articleNumber: string): void {
    this.form.controls.articleNumberLongTextInput.setValue(articleNumber);
    this.form.controls.articleNumberTextInput.setValue(articleNumber);
  }

  public validateForm(): boolean {
    if (this.form.errors || !this.form.valid) {
      return false;
    }
    if (!this.form.controls.typeClassIdCombobox.value) {
      return false;
    }
    if (!this.form.controls.mainNoteCombobox.value) {
      return false;
    }
    if (this.mcsGds221LtlExport && !this.form.controls.mainNoteCombobox.value) {
      return false;
    }
    if (!this.form.controls.productionPlantCombobox.value) {
      return false;
    }
    if ( this.isCountryCodeSelectorEnabled &&
      (!this.form.controls.countryCodeCombobox.value || (isArray(this.form.controls.countryCodeCombobox.value) && this.form.controls.countryCodeCombobox.value.length < 1))) {
      return false;
    }
    if (this.mcsGds221LtlExport && !this.form.controls.productFamilyCombobox.value) {
      return false;
    }
    return true;
  }

}
